import { isFullPageShopifyReturn } from '@/flows/shopifyOnboardingInstall';
import { isShopifyInstallPage, readShopifyInstallContext } from '@/flows/shopifyInstall';
import { buildOnboardingRoutePreservingInvite } from '@/components/Onboarding/inviteRedirect';
import type { OnboardingResumeWorkspace } from '@common/api/authService';
import type { Workspace, WorkspaceRole } from './useWorkspaces';

const SHOPIFY_PROVIDER = 'shopify';
const ONBOARDING_CONNECT_STEP = 'connect';

export const shouldHideShopifyCallback = ({ currentSearch }: { currentSearch: string }): boolean => {
    const searchParams = new URLSearchParams(currentSearch);
    return !isFullPageShopifyReturn(currentSearch) && searchParams.get('connect_success')?.trim().toLowerCase() === SHOPIFY_PROVIDER;
};

export const normalizeShopifyOnboardingResumeWorkspace = (
    workspace?: OnboardingResumeWorkspace | null,
): Workspace | null => {
    if (!workspace?.id) {
        return null;
    }

    return {
        id: workspace.id,
        name: workspace.name,
        logo_url: workspace.logoUrl || undefined,
        owner_id: workspace.ownerId,
        settings: workspace.settings || undefined,
        status: workspace.status as Workspace['status'],
        created_at: workspace.createdAt,
        updated_at: workspace.updatedAt,
        role: (workspace.currentUserRole || 'OWNER').toUpperCase() as WorkspaceRole,
        subscription: null,
    };
};

export const buildShopifyOnboardingResumeRoute = (
    currentSearch: string,
    workspaceId: string,
): string => {
    const onboardingRoute = buildOnboardingRoutePreservingInvite(currentSearch);
    const [pathname, rawSearch = ''] = onboardingRoute.split('?');
    const searchParams = new URLSearchParams(rawSearch);

    searchParams.set('resume_workspace_id', workspaceId);
    searchParams.set('resume_step', ONBOARDING_CONNECT_STEP);

    return `${pathname}?${searchParams.toString()}`;
};

export const getShopifyOnboardingResumeWorkspaceId = (currentSearch: string): string => {
    const searchParams = new URLSearchParams(currentSearch);
    const resumeWorkspaceId = searchParams.get('resume_workspace_id')?.trim();
    if (resumeWorkspaceId) {
        return resumeWorkspaceId;
    }

    const connectSuccess = searchParams.get('connect_success')?.trim().toLowerCase();
    if (connectSuccess !== SHOPIFY_PROVIDER) {
        return '';
    }

    return searchParams.get('workspace_id')?.trim() || '';
};

export const shouldResolveShopifyOnboardingResume = ({
    currentSearch,
    workspaceCount,
    hasPendingPaymentWorkspace,
    currentPathname = '',
}: {
    currentSearch: string;
    workspaceCount: number;
    hasPendingPaymentWorkspace: boolean;
    currentPathname?: string;
}): boolean => {
    if (isShopifyInstallPage(currentPathname)) {
        return Boolean(readShopifyInstallContext(currentSearch) && getShopifyOnboardingResumeWorkspaceId(currentSearch));
    }
    if (hasPendingPaymentWorkspace) {
        return false;
    }

    if (workspaceCount === 0) {
        return true;
    }

    return Boolean(getShopifyOnboardingResumeWorkspaceId(currentSearch));
};

export const shouldKeepShopifyOnboardingResumeInactive = (currentSearch: string, currentPathname = ''): boolean => {
    if (isShopifyInstallPage(currentPathname)) return false;
    const searchParams = new URLSearchParams(currentSearch);
    return searchParams.get('resume_step')?.trim().toLowerCase() === ONBOARDING_CONNECT_STEP;
};

export const buildShopifyOnboardingConnectRedirectUrl = (
    currentUrl: string,
    workspaceId?: string | null,
): string => {
    const redirectUrl = new URL(currentUrl);
    const normalizedWorkspaceId = workspaceId?.trim();

    redirectUrl.searchParams.set('connect_success', SHOPIFY_PROVIDER);
    if (normalizedWorkspaceId) {
        redirectUrl.searchParams.set('workspace_id', normalizedWorkspaceId);
        redirectUrl.searchParams.set('resume_workspace_id', normalizedWorkspaceId);
        redirectUrl.searchParams.set('resume_step', ONBOARDING_CONNECT_STEP);
    }

    return redirectUrl.toString();
};

// Inventory hydration can select a real prepared workspace for the install page,
// while the page remains the sole navigation owner. No activation occurs here.
export function getShopifyOnboardingResumeDestination(currentPathname: string, search: string, workspaceId: string): string | null {
    return isShopifyInstallPage(currentPathname) ? null : buildShopifyOnboardingResumeRoute(search, workspaceId);
}
